/* LaunchFlow: rising-fade reveal (replayable, bidirectional)
   ---------------------------------------------------------------------------
   Scoped deliberately narrowly: only [data-lf-reveal] elements animate, which
   in practice is the lf-image-text-banner sections. Video banners, showcase
   banners, subcategory tiles and product cards are intentionally NOT animated.

   v16 behaviour change — the reveal is no longer a one-shot:

   * It REPLAYS. Scroll down past the trigger line and the banner rises and
     fades in; scroll back up past the same line and it falls and fades back
     out; scroll down again and it plays again. v15 unobserved the element the
     first time it fired, so the animation could only ever be seen once.
   * ONE TRIGGER LINE, both directions. The banner flips state the moment its
     top edge crosses a fixed horizontal line in the viewport, at
     `TRIGGER_LINE` of the viewport height. Because the enter and the exit are
     the same line, the banner appears and disappears at exactly the same
     scroll position — which is what makes the reverse read as a reverse and
     not as a second, unrelated effect.
   * The line is expressed as a FRACTION of the viewport, not as a fraction of
     the element. v15 used `threshold: 0.15`, so the trigger point moved with
     the height of the banner and a short banner fired at a different scroll
     depth than a tall one. A viewport-relative line behaves identically for
     every banner and every screen size.

   Design notes carried over from v15, since several are easy to get wrong:

   * Fail visible. The armed (hidden) state is applied by THIS SCRIPT, never by
     the stylesheet. If the JS never runs — blocked, errored, old browser — the
     element has no `.lf-reveal-armed` class and renders fully visible. Setting
     opacity:0 in CSS as the default would hide the content permanently for
     those users.
   * No layout shift. Only `opacity` and `transform` are animated. Both are
     composited and neither affects document height, so the pre-reveal state
     reserves exactly the same space as the final state.
   * prefers-reduced-motion: the element is never armed and never observed, so
     it paints in its final state with no transition at all.
   * Re-initialises on shopify:section:load so it works in the theme editor,
     matching the self-initialising pattern in lf-cl2-grid.js.

   One bug class the replay design removes for free: v15 needed a `sweepPassed`
   helper bound to hashchange/pageshow because IntersectionObserver only fires
   when a threshold is CROSSED, so a viewport that jumped clean over a banner
   left it armed and invisible forever. Here "armed" is the correct state for a
   banner that is off screen, and the observer fires normally the next time the
   element re-enters the viewport, so a missed callback self-heals. `syncAll()`
   below is kept only as cheap insurance for restored scroll positions.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  // Each lf-image-text-banner section emits its own <script src> tag, so on a
  // page with three banners this file executes three times. Wire the global
  // listeners once; init() is still safe to re-run and is re-invoked on
  // shopify:section:load.
  var alreadyBooted = window.__lfRevealBooted;
  window.__lfRevealBooted = true;

  var SELECTOR = '[data-lf-reveal]';

  /* Where the banner flips, as a fraction of the viewport height measured from
     the top. 0.66 puts the line two thirds down the screen: the banner starts
     animating once its top edge has risen past that point, so roughly a third
     of it is already on screen and the motion happens well inside the visible
     area rather than right at the bottom edge.

     v15 fired at an effective ~0.80 of the viewport (a 0.92 root bottom minus
     15% of a 644px-tall banner), which is why the animation was mostly over
     before it was properly in view. */
  var TRIGGER_LINE = 0.646;

  /* rootMargin needs the complement: trimming the root's bottom edge by
     (1 - TRIGGER_LINE) leaves an observation box whose bottom IS the line.
     Kept to two decimals rather than rounded to a whole percent — a 1%
     rounding error is 8-12px of scroll on a normal screen, which is the same
     order as the adjustment being made here. Measured across 600/720/842/
     1000/1200px viewports the banner actually flips at 0.657 +/- 0.006 of the
     viewport height, i.e. the browser lands about 1% below the nominal figure;
     0.646 is chosen so the measured result is the 0.657 that was wanted. */
  var ROOT_MARGIN = '0px 0px -' + ((1 - TRIGGER_LINE) * 100).toFixed(2) + '% 0px';

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function reveal(el) {
    el.classList.remove('lf-reveal-armed');
    el.classList.add('lf-reveal-in');
  }

  function arm(el) {
    el.classList.remove('lf-reveal-in');
    el.classList.add('lf-reveal-armed');
  }

  /* Arm without animating — used ONLY for the state a banner is given at init.
     The unarmed banner paints at opacity 1, so arming it is a change from
     visible to hidden, and now that the transition is declared on the armed
     state too (that is what makes the scroll-up reverse animate) that change
     would play as a 624ms fade-OUT the moment the page loads. Invisible for a
     banner far below the fold; very visible for one sitting just under the
     trigger line, where a third of it is on screen and would melt away on
     load. `.lf-reveal-instant` suppresses the transition for exactly one
     frame, so the armed state is simply the state the banner starts in.

     The rAF pair is deliberate: one frame to let the armed styles be applied,
     a second to let them be painted, and only then is animation re-enabled.
     The timeout is a fallback for background tabs, where rAF may not run;
     removing the class twice is harmless. */
  function armInitial(el) {
    el.classList.add('lf-reveal-instant');
    arm(el);
    var release = function () { el.classList.remove('lf-reveal-instant'); };
    if (window.requestAnimationFrame) {
      requestAnimationFrame(function () { requestAnimationFrame(release); });
    }
    setTimeout(release, 200);
  }

  function revealAll(root) {
    var els = (root || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < els.length; i++) reveal(els[i]);
  }

  /* True when the element's top edge is above the trigger line and the element
     has not yet scrolled off the top of the screen — the same test the
     observer's rootMargin encodes, computed directly from geometry. */
  function isPastLine(el) {
    var r = el.getBoundingClientRect();
    return r.top < window.innerHeight * TRIGGER_LINE && r.bottom > 0;
  }

  var observer = null;

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Note: NO unobserve. The element stays observed for the life of the
          // page so the animation plays again on every pass.
          if (entry.isIntersecting) {
            reveal(entry.target);
          } else {
            arm(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: ROOT_MARGIN }
    );
    return observer;
  }

  function init(root) {
    var scope = root || document;
    var els = scope.querySelectorAll(SELECTOR);
    if (!els.length) return;

    // No IntersectionObserver, or the visitor asked for reduced motion:
    // show everything immediately, unanimated.
    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      revealAll(scope);
      return;
    }

    var obs = getObserver();

    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.__lfRevealWired) continue;
      el.__lfRevealWired = true;

      /* Set the starting state from geometry BEFORE observing. A banner that
         is already past the line at load — above the fold, or a restored deep
         scroll position — is revealed outright rather than armed, so it is
         never briefly painted at opacity 0 and then flashed in. Everything
         else arms, off screen, where arming is invisible. The observer's first
         callback arrives immediately after and agrees with whichever state we
         picked here, so nothing moves. */
      if (isPastLine(el)) {
        reveal(el);
      } else {
        armInitial(el);
      }

      obs.observe(el);
    }
  }

  /* Insurance for instantaneous jumps — an in-page anchor, or the browser
     restoring a scroll position on back/forward — where the viewport can move
     without the observer being handed a crossing to report. Re-derives every
     banner's state from live geometry. Not a scroll listener: it runs only on
     the two events that can move the viewport in a single frame. */
  function syncAll() {
    if (!('IntersectionObserver' in window) || prefersReducedMotion()) return;
    var els = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < els.length; i++) {
      if (isPastLine(els[i])) {
        reveal(els[i]);
      } else {
        arm(els[i]);
      }
    }
  }

  if (!alreadyBooted) {
    window.addEventListener('hashchange', syncAll);
    window.addEventListener('pageshow', syncAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
